import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { useCountryOptions } from '@/hooks/useDropdowns';
import type { FacilityProcessGroupOption } from '@/hooks/useGroupedFacilityProcessOptions';
import { useGroupedFacilityProcessOptions } from '@/hooks/useGroupedFacilityProcessOptions';
import { useTranslator } from '@/hooks/useTranslator';
import { AttachmentTemplateForm } from '../../../-components/AttachmentTemplateForm';
import { useCreateAdminAttachmentTemplate } from '../../../-hooks/useCreateAdminAttachmentTemplate';
import type { AttachmentTemplateFormValues } from '../../../-types';
import { toTemplatePayload } from '../../../-utils/attachmentTemplateTransformers';

export const Route = createFileRoute(
  '/(client)/orders/(pages)/attachment-templates/create/',
)({
  component: CreateAttachmentTemplatePage,
});

function CreateAttachmentTemplatePage() {
  const { getTranslation: t } = useTranslator({ page: 'attachmentTemplates' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mutation = useCreateAdminAttachmentTemplate();
  const { data: fpGroups = [] } = useGroupedFacilityProcessOptions();
  const { data: countryOptions = [] } = useCountryOptions();

  const allFpKeys = (fpGroups as FacilityProcessGroupOption[]).flatMap(g =>
    g.options.map(o => o.key),
  );
  const allCountryCodes = countryOptions.map(c => c.key);

  const handleSubmit = async (values: AttachmentTemplateFormValues) => {
    try {
      const payload = toTemplatePayload(values, allFpKeys, allCountryCodes);
      const result = await mutation.mutateAsync(payload);
      toast.success(t('create.success'));
      await queryClient.invalidateQueries({
        queryKey: ['orders', 'adminAttachmentTemplates'],
      });
      void navigate({
        to: '/orders/attachment-templates/$id',
        params: { id: result.id },
      });
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  return (
    <AttachmentTemplateForm
      onSubmit={handleSubmit}
      isPending={mutation.isPending}
      submitLabel={t('create.title')}
      fpGroups={fpGroups as FacilityProcessGroupOption[]}
      countryOptions={countryOptions}
    />
  );
}
